import React from 'react';
import _ from 'lodash';
import { ensurePluginOrder, Hooks, TableInstance, Row } from 'react-table';

const pluginName = 'useStaticGroupBy';

function useInstance<D extends object>(instance: TableInstance<D>) {
    const { plugins, rows, state } = instance;
    ensurePluginOrder(plugins, ['useGroupBy', 'useExpanded'], pluginName);
    const overridesVirtualGroupIdentifier = 'virtualRuleGroup:Overrides';

    const tableColumns = instance.allColumns;
    const hasGroup = state.groupBy && state.groupBy.length > 0;

    const groupedOrderByRows = React.useMemo(
        () =>
            hasGroup
                ? rows.map((row) => {
                      if (row.groupByID && row.groupByOrder === undefined) {
                          const found = tableColumns.find((aCol) => aCol.id === row.groupByID);
                          if (found && found?.groupByOrder) {
                              // the column will need to have an object that is in the shape of
                              // { "possibleAccessorValue": 1, "anotherAccessorValue": 0 }
                              // so that we can prioritize specific grouped rows
                              // based on the accessor values that may exist
                              row.groupByOrder = _.get(found?.groupByOrder, row.groupByVal);
                          } else {
                              row.groupByOrder = row.id;
                          }
                      }

                      return row;
                  })
                : rows,
        [rows, tableColumns, hasGroup]
    );

    const sortedRows = React.useMemo(() => {
        // no point in mapping if there's no group by I guess
        if (!hasGroup) return groupedOrderByRows;
        const expandedAndSortedRows = [...groupedOrderByRows]
            .filter((row) => {
                return row.groupByOrder != null;
            })
            .sort((rowA, rowB) => {
                // always show the override group at the very top. sort the rest normally.
                if (rowA.groupByOrder === overridesVirtualGroupIdentifier) {
                    return -1;
                }
                if (rowB.groupByOrder === overridesVirtualGroupIdentifier) {
                    return 1;
                }
                return rowA.groupByOrder < rowB.groupByOrder ? -1 : 1;
            })
            .reduce((acc, curr) => {
                return [...acc, curr, ...(curr?.isExpanded ? curr.subRows : [])];
            }, [] as Row<D>[]);

        return expandedAndSortedRows;
    }, [groupedOrderByRows, hasGroup, overridesVirtualGroupIdentifier]);

    Object.assign(instance, {
        rows: sortedRows
    });
}

export const useStaticGroupBy = <D extends object = {}>(hooks: Hooks<D>) => {
    hooks.useInstance.push(useInstance);
};

useStaticGroupBy.pluginName = pluginName;
